import {
  basicInfoAtom,
  changeCarListAtom,
  destDetailAtom,
  mapCenterCoorAtom,
  resultDestListAtom,
  shippingCompleteAtom,
  shippingListAtom,
  timeLineChgArrAtom,
  timeLineOrgArrAtom,
} from '@/commons/constants';
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import * as _ from 'lodash';
import * as df from 'date-fns';
import { Button, Divider, Modal, Popover, Tooltip, Typography } from '@mui/material';
import { TimeLine, TimeLineItem } from '@/types/result';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import {
  createTimeBase,
  createTimelineMin,
  destListCarSeqUpdate,
  getTimeLineDestInfo,
  getTimelineLength,
} from '@/commons/utils';
import { DataGrid, GridCloseIcon, GridColDef } from '@mui/x-data-grid';

const MainWrap = styled.div`
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;
const SubWrap = styled.div<{ size: number }>`
  background: #eeeeee;
  width: calc(${({ size }) => (size && size > 0 ? size * 1.25 : 0)}rem + 2rem);
  min-width: 400px;
  min-height: 3rem;
  display: flex;
  gap:0;
  align-items: center;
  // justify-content: flex-start;
  border-bottom: 1.5px solid #444499;
  padding:0 1rem;
  > div {
    font-size: 1rem;
    width: 1.25rem;
    height: 1.5rem;
    min-height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .time{
    display:flex;
    flex-direction: column;
    justify-content:space-between;
    align-items:flex-start;
    min-width: calc(1.25rem * 10);
    min-height: calc(1.5rem - 1px);
    height: calc(1.5rem - 1px);
    .text{
      margin-left:-1rem;
      font-size: 0.6rem;
    }
    .line{
      flex-grow:1;
      width: 2px;
      border-left: 1px solid #555555;
    }
  }
`;

export default function MinTimeLine() {
  const basicInfo = useAtomValue(basicInfoAtom);
  const shippingList = useAtomValue(shippingListAtom);

  const [size, setSize] = useState<number>(0);
  const setOriginArray = useSetAtom(timeLineOrgArrAtom);

  const [copyArray, setCopyArray] = useAtom(timeLineChgArrAtom);

  useEffect(() => {
    if (basicInfo && shippingList) {
      const originList: TimeLine[] = createTimelineMin(basicInfo, shippingList);
      const timelineSize = getTimelineLength(basicInfo.timeLine.timelineLength);
      setSize(timelineSize);
      setOriginArray(originList);
      setCopyArray(originList);
    } else {
      setOriginArray([]);
      setCopyArray([]);
    }
  }, [basicInfo, shippingList]);

  const timeBase = useMemo(
    () => createTimeBase(basicInfo?.timeLine.startHour, basicInfo?.timeLine.endHour, 10),
    [basicInfo?.timeLine.startHour, basicInfo?.timeLine.endHour],
  );

  return (
    <MainWrap>
      <SubWrap size={size} style={{ background: '#ffffff', minHeight: '1.5rem' }}>
        {timeBase.map((item, index) => (
          <div key={index} className="time">
            <span className="text">{df.format(item, 'HH:mm')}</span>
            <div className="line" />
          </div>
        ))}
      </SubWrap>
      {copyArray &&
        copyArray.length > 0 &&
        copyArray.map((item: TimeLine) => (
          <SubWrap key={item.car.car_seq} size={size}>
            {item.timelineList.map((tlItem: any, ti: number) => {
              return <Marking key={ti} item={tlItem} />;
            })}
          </SubWrap>
        ))}
    </MainWrap>
  );
}

const PopoverContent = ({
  item,
  store,
  onClose,
  onReOrder,
  onDelete,
  onAddDest,
}: {
  item: TimeLineItem;
  store: ReturnType<typeof useStore>;
  onClose: () => void;
  onReOrder: (target: TimeLineItem, dest: TimeLineItem) => void;
  onDelete: () => void;
  onAddDest: () => void;
}) => {
  const hasSelectedTarget = useAtomValue(hasSelectedTargetAtom);
  const setSelectedTarget = useSetAtom(selectedTargetAtom);

  return (
    <div style={{ padding: '0.5rem', display: 'flex', gap: '0.5rem' }}>
      {item.target ? (
        <>
          {!hasSelectedTarget ? (
            <Button variant="contained" onClick={() => { setSelectedTarget(item); onClose(); }}>이동</Button>
          ) : (
            <>
              <Button variant="contained" color="secondary" onClick={() => onReOrder(store.get(selectedTargetAtom), item)}>추가</Button>
              <Button variant="contained" color="warning" onClick={() => { setSelectedTarget(undefined); onClose(); }}>취소</Button>
            </>
          )}
          {!hasSelectedTarget && (
            <Button variant="contained" color="error" onClick={onDelete}>삭제</Button>
          )}
        </>
      ) : hasSelectedTarget ? (
        <>
          <Button variant="contained" color="secondary" onClick={() => onReOrder(store.get(selectedTargetAtom), item)}>추가</Button>
          <Button variant="contained" color="warning" onClick={() => { setSelectedTarget(undefined); onClose(); }}>취소</Button>
        </>
      ) : (
        <Button variant="contained" color="secondary" onClick={onAddDest}>미배정 추가</Button>
      )}
    </div>
  );
};

const MarkingWrap = styled.div<{ color: string; size: number }>`
  cursor: pointer;
  background: ${({ color }) => color};
  width: calc(1.25rem * ${({ size }) => size} - 3px);
  min-width: calc(1.25rem * ${({ size }) => size} - 3px);
  // font-size: ${({ size }) => (size > 2 ? '1rem' : '0.6rem')} !important;
  height: 100%;
  border-radius: 8px;
  color: #ffffff;
  margin: 0 1.5px;
  // border: 1px solid black;
`;

const Line = styled.div<{ color: string }>`
  cursor: cell;
  width: 1.25rem;
  min-width: 1.25rem;
  height: 100%;
  // border: 1px solid black;
  > div {
   width: 100%;
   background: ${({ color }) => color};
   height: 3px;
  }
`;

const selectedTargetAtom = atom<TimeLineItem>();
// 선택된 타겟이 있는지 여부만 구독 (이동 버튼 표시용)
const hasSelectedTargetAtom = atom((get) => get(selectedTargetAtom) != null);

const Marking = memo(({ item }: { item: TimeLineItem }) => {
  const store = useStore();
  const setDestDetail = useSetAtom(destDetailAtom);
  const setMapCenterCoor = useSetAtom(mapCenterCoorAtom);
  const setCopyArray = useSetAtom(timeLineChgArrAtom);
  const setChangeCarList = useSetAtom(changeCarListAtom);

  // 이 아이템이 선택된 타겟인지만 구독 → 다른 Marking 재렌더링 방지
  const isSelectedTarget = useAtomValue(
    useMemo(() => atom((get) => get(selectedTargetAtom)?.index === item.index), [item.index]),
  );
  const setSelectedTarget = useSetAtom(selectedTargetAtom);
  const setResultDestList = useSetAtom(resultDestListAtom);
  const isComplete = useAtomValue(shippingCompleteAtom);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [destAddOpen, setDestAddOpen] = useState<boolean>(false);

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleReOrder = useCallback((target: TimeLineItem, dest: TimeLineItem) => {
    setSelectedTarget(undefined);
    handlePopoverClose();
    const copyArray = store.get(timeLineChgArrAtom);
    const destCarIndex = copyArray.findIndex((slList: TimeLine) => slList.car.car_seq === dest.car.car_seq);
    const targetCarIndex = copyArray.findIndex((slList: TimeLine) => slList.car.car_seq === target.car.car_seq);

    const destTimeline: TimeLineItem[] = copyArray[destCarIndex].timelineList;
    let targetTimeline: TimeLineItem[] = copyArray[targetCarIndex].timelineList;

    const dFIndex = destTimeline.findIndex((tlItem: TimeLineItem) => tlItem.index === dest.index);
    const tFIndex = targetTimeline.findIndex((fItem: TimeLineItem) => fItem.index === target.index);

    let chgDestTimeline: TimeLineItem[] = [];
    if (destCarIndex !== targetCarIndex || !target.index) {
      chgDestTimeline = [
        ...destTimeline.slice(0, dFIndex),
        {
          ...target,
          car: destTimeline[0].car,
        },
        ...destTimeline.slice(dFIndex, destTimeline.length),
      ].reverse();

      for (let i = 0; i < target.target.dwellTime.value / 60; i++) {
        chgDestTimeline = chgDestTimeline.filter(
          (fItem: TimeLineItem) => fItem.index !== chgDestTimeline.find((rItem: TimeLineItem) => !rItem.target).index,
        );
      }
      chgDestTimeline = chgDestTimeline.reverse();
      const targetTempLine = [];
      for (let i = 0; i < target.target.dwellTime.value / 60; i++) {
        targetTempLine.push({
          ...target,
          color: targetTimeline[0].color,
          index: targetCarIndex + '_' + (tFIndex + i),
          target: undefined,
        });
      }
      targetTimeline = [
        ...targetTimeline.slice(0, tFIndex),
        ...targetTempLine,
        ...targetTimeline.slice(tFIndex + 1, targetTimeline.length),
      ];
    } else {
      chgDestTimeline = destTimeline.filter((fItem: any) => fItem.index !== target.index);

      chgDestTimeline = [
        ...chgDestTimeline.slice(0, dFIndex),
        {
          ...target,
          car: destTimeline[0].car,
        },
        ...chgDestTimeline.slice(dFIndex, chgDestTimeline.length),
      ];
    }
    const retArray = copyArray.map((item: TimeLine, index: number) => {
      let timelineList = item.timelineList;
      if (index === destCarIndex) {
        timelineList = chgDestTimeline;
        const { destList, destLength, orderLength } = getTimeLineDestInfo(timelineList);
        return {
          ...item,
          destList,
          destLength,
          orderLength,
          distance: 0,
          time: 0,
          timelineList,
        };
      } else if (index === targetCarIndex && destCarIndex !== targetCarIndex) {
        timelineList = targetTimeline;
        const { destList, destLength, orderLength } = getTimeLineDestInfo(timelineList);
        return {
          ...item,
          destList,
          destLength,
          orderLength,
          distance: 0,
          time: 0,
          timelineList,
        };
      } else {
        return item;
      }
    });

    const tempList = [...store.get(changeCarListAtom)];
    tempList.push(target.car.car_seq);
    tempList.push(dest.car.car_seq);
    setChangeCarList(_.uniq(tempList));
    setCopyArray(retArray);
    setResultDestList(destListCarSeqUpdate(store.get(resultDestListAtom), target.target.id, dest.car.car_seq));
  }, [store, setSelectedTarget, setChangeCarList, setCopyArray, setResultDestList]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (item.target) {
      setDestDetail({ car: item.car, color: item.color, dest: item.target });
      setMapCenterCoor(item.target.coord);
    }
    if (!isComplete) {
      setAnchorEl(event.currentTarget);
    }
  }, [isComplete, item, setDestDetail, setMapCenterCoor]);

  const open = Boolean(anchorEl);

  const handleDeleteClick = useCallback(() => {
    handlePopoverClose();
    const copyArray = store.get(timeLineChgArrAtom);
    const targetCarIndex = copyArray.findIndex((slList: TimeLine) => slList.car.car_seq === item.car.car_seq);
    let targetTimeline = copyArray[targetCarIndex].timelineList;

    const tFIndex = targetTimeline.findIndex((fItem: TimeLineItem) => fItem.index === item.index);
    targetTimeline[tFIndex] = {
      ...item,
      target: undefined,
    };
    for (let i = 1; i < (item.target.dwellTime.value > 0 ? item.target.dwellTime.value / 60 : 1); i++) {
      targetTimeline = [
        ...targetTimeline.slice(0, tFIndex + i),
        {
          ...item,
          color: targetTimeline[0].color,
          index: targetCarIndex + '_' + (tFIndex + i),
          target: undefined,
          car: targetTimeline[0].car,
        },
        ...targetTimeline.slice(tFIndex + i, targetTimeline.length),
      ];
    }
    const retArray = copyArray.map((citem: TimeLine, index: number) => {
      let timelineList = citem.timelineList;
      if (index === targetCarIndex) {
        timelineList = targetTimeline;
        const { destList, destLength, orderLength } = getTimeLineDestInfo(timelineList);
        return {
          ...citem,
          destList,
          destLength,
          orderLength,
          distance: 0,
          time: 0,
          timelineList,
        };
      } else {
        return citem;
      }
    });
    const tempList = [...store.get(changeCarListAtom)];
    tempList.push(item.car.car_seq);
    setChangeCarList(_.uniq(tempList));
    setCopyArray(retArray);
    setResultDestList(destListCarSeqUpdate(store.get(resultDestListAtom), item.target.id, null));
  }, [item, store, handlePopoverClose, setChangeCarList, setCopyArray, setResultDestList]);

  return (
    <>
      {item.target ? (
        <MarkingWrap
          aria-describedby={item.index}
          size={item.target.dwellTime.value > 0 ? item.target.dwellTime.value / 60 : 1}
          color={item.color}
          onClick={handleClick}
          style={{
            opacity: isSelectedTarget ? 0.3 : 1,
          }}
        >
          <Tooltip title={item.target.name + ' : ' + item.target.arrivalTime}>
            {item.target.delivery_no !== 'A' ? (
              <Typography component="span">{item.target.delivery_no}</Typography>
            ) : (
              <Typography component="span">
                <DownloadDoneIcon />
              </Typography>
            )}
          </Tooltip>
        </MarkingWrap>
      ) : (
        <Line color={item.color} onClick={handleClick}>
          <div>&nbsp;</div>
        </Line>
      )}
      {open && (
        <Popover
          id={item.index}
          open={true}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={handlePopoverClose}
        >
          <PopoverContent
            item={item}
            store={store}
            onClose={handlePopoverClose}
            onReOrder={handleReOrder}
            onDelete={handleDeleteClick}
            onAddDest={() => setDestAddOpen(true)}
          />
        </Popover>
      )}
      {destAddOpen && (
        <AddDest
          open={destAddOpen}
          item={item}
          resultDestList={store.get(resultDestListAtom)}
          handleReOrder={handleReOrder}
          handleClose={() => setDestAddOpen(false)}
        />
      )}
    </>
  );
});

const ModalWrap = styled.div`
  position: absolute;
  top:50%;
  left:50%;
  transform: translate(-50%, -50%);
  width: 40%;
  height: 33rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  .head{
    padding: 0.5rem;
    width: 100%;
    height: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background:rgba(82, 133, 164, 0.87);
    color: #ffffff;
  }
  .body{
    padding: 1rem;
    width: 100%;
    flex-grow: 1;
    overflow:hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
  }
  .button{
    padding: 0 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
`;

const AddDest = ({
  open,
  item,
  resultDestList,
  handleReOrder,
  handleClose,
}: { open: boolean; item: TimeLineItem; resultDestList: any[]; handleReOrder: any; handleClose: any }) => {
  const [contents, setContents] = useState<any[]>();

  useEffect(() => {
    if (resultDestList) {
      const temp: any = [];
      _.forEach(resultDestList, (val) => {
        if (val.r_car_seq === null) {
          const retVal = {
            ...val,
            car: item.car,
            color: '#bbbbbb',
            target: {
              id: val.seq,
              name: val.name,
              address: val.address,
              lat: val.lat,
              lng: val.lng,
              coord: new naver.maps.LatLng({ lat: val.lat, lng: val.lng }),
              dwellTime: { value: val.dwell_time },
              orderInfo: val.order_info.list,
              region: val.region,
              workTime: val.work_time,
              delivery_no: 'A',
            },
            id: temp.length + 1,
            orderCount: val.order_info.list.length,
            deliverTime: val.work_time.start_time + ' ~ ' + val.work_time.end_time,
            dwellTime: val.dwell_time / 60 + '분',
          };
          temp.push(retVal);
        }
      });
      setContents(temp);
    }
  }, [resultDestList]);
  const column: GridColDef[] = [
    { field: 'id', headerName: 'No', width: 30, headerAlign: 'center', align: 'right' },
    { field: 'name', headerName: '배송지', flex: 1, headerAlign: 'center' },
    { field: 'address', headerName: '주소', flex: 2, headerAlign: 'center' },
    { field: 'orderCount', headerName: '주문건수', width: 60, headerAlign: 'center', align: 'center' },
    { field: 'deliverTime', headerName: '배송가능시간', width: 100, headerAlign: 'center', align: 'center' },
    { field: 'dwellTime', headerName: '체류시간', width: 60, headerAlign: 'center', align: 'center' },
  ];
  return (
    <Modal open={open} onClose={handleClose}>
      <ModalWrap>
        <div className="head">
          <Typography variant="h5">배송지 추가(매배정 목록)</Typography>
          <GridCloseIcon onClick={handleClose} />
        </div>
        <Divider />
        <div className="body">
          <DataGrid
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            columns={column}
            onRowClick={(target) => {
              handleReOrder(target.row, item);
              handleClose();
            }}
            rows={contents && contents.length > 0 ? contents : []}
          />
        </div>
      </ModalWrap>
    </Modal>
  );
};
